import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
	return twMerge(clsx(inputs));
}

export const API_URL =
	process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

// Guard against multiple concurrent 401 redirects
let _isRedirecting = false;

// Session expiry handler - redirects to login on 401/403
function handleSessionExpired() {
	if (_isRedirecting) return;
	_isRedirecting = true;
	localStorage.removeItem("session_token");
	// Only redirect if not already on login page
	if (!window.location.pathname.includes("/login")) {
		window.location.href = "/login?session_expired=true";
	}
	// Reset after a short delay in case redirect doesn't happen (e.g., already on login)
	setTimeout(() => {
		_isRedirecting = false;
	}, 2000);
}

export async function fetchAPI(endpoint, options = {}) {
	const url = `${API_URL}/api${endpoint}`;

	// Merge headers properly: default → caller → auth token
	const isFormData =
		typeof FormData !== "undefined" && options.body instanceof FormData;
	const headers = {
		...(!isFormData ? { "Content-Type": "application/json" } : {}),
		...options.headers,
	};

	const token = localStorage.getItem("session_token");
	if (token) {
		headers["Authorization"] = `Bearer ${token}`;
	}

	const { headers: _discardedHeaders, ...restOptions } = options;
	const fetchOptions = {
		credentials: "include",
		...restOptions,
		headers,
	};

	const response = await fetch(url, fetchOptions);

	const parseResponseBody = async () => {
		if (response.status === 204) return null;
		const text = await response.text().catch(() => "");
		if (!text) return null;
		try {
			return JSON.parse(text);
		} catch {
			return null;
		}
	};

	// Handle session expiry globally
	if (response.status === 401) {
		const error = (await parseResponseBody()) || { detail: "Session expired" };
		const detail = (error.detail || "").toLowerCase();
		// Only redirect for session/auth issues, not wrong credentials during login
		if (
			detail.includes("authenticated") ||
			detail.includes("expired") ||
			detail.includes("invalid token") ||
			detail.includes("not authenticated")
		) {
			handleSessionExpired();
			throw new Error("La sessione è scaduta. Effettua nuovamente il login.");
		}
		throw new Error(error.detail || "Not authorized");
	}

	if (response.status === 403) {
		const error = (await parseResponseBody()) || { detail: "Not authorized" };
		const err = new Error(
			error.detail || error.error_description || "Not authorized",
		);
		err.status = 403;
		err.detail = error.detail;
		throw err;
	}

	if (!response.ok) {
		const error = (await parseResponseBody()) || { detail: "Request failed" };
		// Le rotte OAuth del connettore MCP rispondono nel formato della
		// specifica (`error` + `error_description`), non con `detail`.
		const err = new Error(
			error.detail || error.error_description || "Request failed",
		);
		err.status = response.status;
		err.detail = error.detail;
		throw err;
	}

	return parseResponseBody();
}

/**
 * Consume a Server-Sent Events (SSE) stream from a POST endpoint.
 * Mirrors fetchAPI auth/URL but reads the body incrementally.
 * Handlers: onStatus, onDelta, onResult, onDone, onError (each receives the parsed JSON data).
 * Resolves when the stream ends. Network/HTTP failures throw (caller should fall back).
 */
export async function streamSSE(endpoint, body, handlers = {}) {
	const {
		onStatus,
		onDelta,
		onResult,
		onDone,
		onError,
		// Catch-all: riceve ogni evento, anche quelli non previsti qui sopra
		// (l'agente AI emette `tool_call` / `tool_result`). Tenerlo generico
		// evita un secondo parser SSE accanto a questo.
		onEvent,
		signal,
	} = handlers;
	const url = `${API_URL}/api${endpoint}`;
	const headers = { "Content-Type": "application/json" };
	const token = localStorage.getItem("session_token");
	if (token) headers["Authorization"] = `Bearer ${token}`;

	const response = await fetch(url, {
		method: "POST",
		credentials: "include",
		headers,
		body: JSON.stringify(body),
		signal,
	});

	if (!response.ok || !response.body) {
		throw new Error(`Stream HTTP ${response.status}`);
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";

	const dispatch = (eventType, data) => {
		onEvent?.(eventType, data);
		if (eventType === "status") onStatus?.(data);
		else if (eventType === "delta") onDelta?.(data);
		else if (eventType === "result") onResult?.(data);
		else if (eventType === "done") onDone?.(data);
		else if (eventType === "error") onError?.(data);
	};

	// eslint-disable-next-line no-constant-condition
	while (true) {
		const { value, done } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });
		let idx;
		while ((idx = buffer.indexOf("\n\n")) !== -1) {
			const rawEvent = buffer.slice(0, idx);
			buffer = buffer.slice(idx + 2);
			let eventType = "message";
			let dataStr = "";
			for (const line of rawEvent.split("\n")) {
				if (line.startsWith("event:")) eventType = line.slice(6).trim();
				else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
			}
			if (!dataStr) continue;
			let data;
			try {
				data = JSON.parse(dataStr);
			} catch {
				data = { raw: dataStr };
			}
			dispatch(eventType, data);
		}
	}
}

// Date formatting follows the UI language. Callers that already have it from
// `useLanguage()` pass it through; the "it" default keeps the historic
// behaviour for the call sites that have not been migrated yet.
export function getDateLocale(language = "it") {
	return language === "it" ? "it-IT" : "en-GB";
}

export function formatDate(dateString, language = "it") {
	// new Date(null) is the epoch: a missing date would read "1 January 1970".
	if (!dateString) return "";
	const date = new Date(dateString);
	return date.toLocaleDateString(getDateLocale(language), {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

export function formatShortDate(dateString, language = "it") {
	const date = new Date(dateString);
	return date.toLocaleDateString(getDateLocale(language), {
		month: "short",
		day: "numeric",
	});
}

export function formatTime(seconds) {
	if (!seconds) return "-";
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// Money is stored as integer minor units (cents). Never format from floats.
export function formatMoney(amountCents, currency = "EUR") {
	const value = (Number(amountCents) || 0) / 100;
	try {
		return new Intl.NumberFormat("it-IT", {
			style: "currency",
			currency: currency || "EUR",
		}).format(value);
	} catch {
		return `${value.toFixed(2)} ${currency || "EUR"}`;
	}
}

function normalizeRestText(rawValue) {
	return String(rawValue || "")
		.trim()
		.replace(/[’′`]/g, "'")
		.replace(/[“”″]/g, '"')
		.replace(/''/g, '"')
		.replace(/\s+/g, " ");
}

export function parseSmartRestSeconds(rawValue) {
	const value = normalizeRestText(rawValue);
	if (!value) return null;

	if (/^\d$/.test(value)) {
		return Number(value) * 60;
	}

	if (/^\d+$/.test(value)) {
		const numeric = Number(value);
		return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
	}

	let match = value.match(/^(\d+):(\d{1,2})$/);
	if (match) {
		const minutes = Number(match[1]);
		const seconds = Number(match[2]);
		if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
		if (minutes < 0 || seconds < 0 || seconds > 59) return null;
		return minutes * 60 + seconds;
	}

	match = value.match(/^(\d+)\s*'\s*$/);
	if (match) {
		return Number(match[1]) * 60;
	}

	match = value.match(
		/^(\d+)\s*'\s*(\d{1,2})\s*(?:"|sec|secs|s|secondi|secondo)?$/i,
	);
	if (match) {
		const minutes = Number(match[1]);
		const seconds = Number(match[2]);
		if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
		if (minutes < 0 || seconds < 0 || seconds > 59) return null;
		return minutes * 60 + seconds;
	}

	match = value.match(/^(\d+)\s*(?:m|min|mins|minuti|minuto|minute)\s*$/i);
	if (match) {
		return Number(match[1]) * 60;
	}

	match = value.match(
		/^(\d+)\s*(?:m|min|mins|minuti|minuto|minute)\s*(\d{1,2})\s*(?:"|sec|secs|s|secondi|secondo)?$/i,
	);
	if (match) {
		const minutes = Number(match[1]);
		const seconds = Number(match[2]);
		if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
		if (minutes < 0 || seconds < 0 || seconds > 59) return null;
		return minutes * 60 + seconds;
	}

	match = value.match(/^(\d+)\s*(?:"|sec|secs|s|secondi|secondo)$/i);
	if (match) {
		const seconds = Number(match[1]);
		return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
	}

	return null;
}

function formatSingleRestDisplay(value) {
	const parsedSeconds = parseSmartRestSeconds(value);
	if (Number.isFinite(parsedSeconds) && parsedSeconds > 0) {
		const minutes = Math.floor(parsedSeconds / 60);
		const seconds = parsedSeconds % 60;
		return `${minutes}'${String(seconds).padStart(2, "0")}"`;
	}

	return normalizeRestText(value);
}

// Separators used to express ranges / alternatives: "2:30/3:00", "2:30-3:00".
const REST_COMPOSITE_SEPARATOR = /(\s*[/\-–—~]\s*)/;

export function formatRestDisplay(restValue) {
	const text = normalizeRestText(restValue);
	if (!text) return text;

	const parts = text.split(REST_COMPOSITE_SEPARATOR);
	if (parts.length > 1) {
		let formattedAny = false;
		const rebuilt = parts.map((part, index) => {
			if (index % 2 === 1) return part.trim(); // separator token
			const segment = part.trim();
			if (!segment) return segment;
			const formatted = formatSingleRestDisplay(segment);
			if (formatted !== segment) formattedAny = true;
			return formatted;
		});
		if (formattedAny) return rebuilt.join("");
	}

	return formatSingleRestDisplay(text);
}

export function normalizeSmartRestInput(rawValue) {
	const rawText = normalizeRestText(rawValue);
	if (!rawText) return null;
	return formatRestDisplay(rawText) || rawText;
}

export function calculateLoad(oneRM, percentage) {
	return Math.round(((oneRM * percentage) / 100) * 2) / 2; // Round to nearest 0.5
}
function normalizeRatioText(rawValue) {
	return String(rawValue || "")
		.trim()
		.replace(/\s+/g, "")
		.toLowerCase();
}

function isLikelyRatio(first, second) {
	// If second value > 5, probably MM:SS not ratio
	// If both are single digits or very small, likely ratio
	// Ratio typically: 1:1, 2:1, 1:2, 3:1, 4:1, 1:3, 2:3, 3:2, etc.
	if (second > 5) return false;
	if (first > 10) return false;
	return first >= 1 && second >= 1;
}

export function parseSmartRatio(rawValue) {
	const value = normalizeRatioText(rawValue);
	if (!value) return null;

	const parts = value.split(":");
	if (parts.length !== 2) return null;

	const first = Number(parts[0]);
	const second = Number(parts[1]);

	if (!Number.isFinite(first) || !Number.isFinite(second)) return null;
	if (first <= 0 || second <= 0) return null;

	// Validate as ratio using heuristic
	if (!isLikelyRatio(first, second)) return null;

	return `${first}:${second}`;
}

export function normalizeSmartRatioInput(rawValue) {
	const parsed = parseSmartRatio(rawValue);
	if (parsed) {
		return parsed;
	}
	// Fallback: return normalized text
	return normalizeRatioText(rawValue);
}

export function getToday() {
	const date = new Date();
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

// Nome completo di un utente. Il `surname` e' stato aggiunto dopo il lancio:
// i profili storici tengono tutto in `name` (surname vuoto) e restano
// invariati, i nuovi iscritti hanno nome + cognome separati. Non introduce mai
// spazi doppi ne' trailing. Un profilo storico che ha poi ricevuto anche il
// `surname` ha il cognome gia' in coda a `name`: non lo si ripete
// ("Andrea Grifoni" + "Grifoni" -> "Andrea Grifoni").
export function formatFullName(user) {
	if (!user) return "";
	const name = String(user.name || "")
		.trim()
		.replace(/\s+/g, " ");
	const surname = String(user.surname || "")
		.trim()
		.replace(/\s+/g, " ");
	if (
		name &&
		surname &&
		name.length > surname.length &&
		name.toLowerCase().endsWith(` ${surname.toLowerCase()}`)
	) {
		return name;
	}
	return [name, surname].filter(Boolean).join(" ");
}

/**
 * Le discipline di un atleta: `sports` quando c'e', altrimenti il campo
 * legacy `sport`. Stessa regola di `athlete_sports()` lato backend — i profili
 * creati prima del multisport hanno solo la primaria, e senza fallback
 * sparirebbero da ogni vista per sport.
 * Restituisce sempre un array: `[]` significa "nessuna disciplina scelta", che
 * e' uno stato bloccante (profilo prestativo vuoto, massimali non inseribili).
 */
export function athleteSportsOf(user) {
	if (!user) return [];
	if (Array.isArray(user.sports) && user.sports.length > 0) return user.sports;
	return user.sport ? [user.sport] : [];
}

/**
 * Seleziona o deseleziona una disciplina nella lista degli sport.
 *
 * Una disciplina nuova va **in coda**: il primo elemento e' la primaria (il tab
 * che il profilo prestativo apre per primo), e aggiungere uno sport non deve
 * cambiarla di nascosto. Togliere la primaria fa salire la seconda.
 * Restituisce sempre un array nuovo, mai quello ricevuto.
 */
export function toggleSportSelection(current, value) {
	const list = Array.isArray(current) ? current.filter(Boolean) : [];
	if (!value) return [...list];
	return list.includes(value)
		? list.filter((item) => item !== value)
		: [...list, value];
}

/**
 * Etichetta leggibile di una disciplina. Le etichette sono dato admin (non si
 * traducono): si leggono da `options` (`/users/sports/list`). Senza una voce
 * corrispondente — disciplina disattivata, lista non ancora caricata — si
 * rende leggibile lo slug invece di mostrarlo grezzo.
 */
export function formatSportLabel(value, options) {
	if (!value) return "";
	const found = (Array.isArray(options) ? options : []).find(
		(option) => option?.value === value,
	);
	if (found?.label) return found.label;
	return String(value)
		.replace(/_/g, " ")
		.replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Le discipline da proporre in un selettore: quelle che si possono scegliere,
 * piu' quelle gia' scelte. `/sports` manda anche gli sport a cui l'utente e'
 * solo legato — disattivati, o personali di un coach che non vede piu' — con
 * `selectable: false`: servono per le etichette, e un selettore deve poterli
 * togliere, non riproporli (docs/sport-config, Fase 3).
 */
export function selectableSports(options, selected = []) {
	const chosen = new Set(
		(Array.isArray(selected) ? selected : []).filter(Boolean),
	);
	return (Array.isArray(options) ? options : []).filter(
		(option) =>
			option && (option.selectable !== false || chosen.has(option.value)),
	);
}

/**
 * Lo slug di uno sport nuovo, con la regola del server
 * (`slugify_discipline` in backend/services/sport_catalog.py): minuscolo,
 * senza accenti, `_` fra le parole, al massimo 40 caratteri. Serve solo
 * all'anteprima: lo slug di uno sport esistente non cambia mai.
 */
export function slugifySport(text) {
	return String(text || "")
		.normalize("NFKD")
		// Come `encode("ascii", "ignore")` del server: via accenti e tutto
		// cio' che non e' ASCII, senza lasciare un separatore al suo posto.
		.replace(/[\u0080-\uffff]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 40)
		.replace(/_+$/g, "");
}
